import { useState, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Copy } from 'lucide-react'
import ErrorBanner from '@/components/ErrorBanner'
import { useJobRuns, useJobStats } from '@/hooks/useJobRuns'
import { useSources } from '@/hooks/useSources'
import { formatTime, relativeTime } from '@/lib/utils'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Pagination,
  Select,
  StatCard,
  Table,
} from '@/admin-design/_common'
import type { JobRun } from '@/api/types'

const PAGE_SIZE = 30

function outcomeBadge(outcome: string) {
  switch (outcome) {
    case 'ok':
      return <Badge variant="success">ok</Badge>
    case 'error':
      return <Badge variant="destructive">error</Badge>
    case 'skipped':
      return <Badge variant="muted">skipped</Badge>
    default:
      return <Badge variant="muted">{outcome || '-'}</Badge>
  }
}

function duration(j: JobRun) {
  if (!j.finished_at || !j.started_at) return '-'
  const sec = j.finished_at - j.started_at
  if (sec < 1) return '<1s'
  return `${sec.toFixed(1)}s`
}

function sourceSlug(name: string) {
  // "source.collect:reddit" → "reddit"
  const i = name.lastIndexOf(':')
  return i >= 0 ? name.slice(i + 1) : name
}

function parseMetrics(raw: string): Record<string, any> {
  if (!raw) return {}
  try {
    return JSON.parse(raw)
  } catch {
    return {}
  }
}

function statValue(value: number | string | undefined | null, loading: boolean): string | number {
  if (loading) return '…'
  if (value === null || value === undefined) return '-'
  return value
}

function ErrorCell({ error }: { error: string }) {
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)

  const onCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(error)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch { /* no-op */ }
  }, [error])

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-left truncate max-w-xs block hover:underline cursor-pointer"
        title="Click to expand"
      >
        {error}
      </button>
    )
  }

  return (
    <div className="space-y-1">
      <pre className="whitespace-pre-wrap break-words text-xs bg-destructive-soft text-destructive-soft-foreground border border-destructive-soft rounded p-2 max-h-48 overflow-y-auto">
        {error}
      </pre>
      <div className="flex gap-1">
        <Button type="button" variant="secondary" size="sm" onClick={onCopy}>
          <Copy size={10} /> {copied ? 'Copied' : 'Copy'}
        </Button>
        <Button type="button" variant="secondary" size="sm" onClick={() => setOpen(false)}>
          Close
        </Button>
      </div>
    </div>
  )
}

function MetricsDetail({ raw }: { raw: string }) {
  const m = parseMetrics(raw)
  const entries = Object.entries(m)
  if (entries.length === 0) return <span className="text-muted-foreground text-xs">no metrics</span>
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1">
      {entries.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-2">
          <span className="text-muted-foreground">{k}</span>
          <span className="font-medium tabular-nums text-foreground">{typeof v === 'number' ? v.toLocaleString('ko-KR') : String(v)}</span>
        </div>
      ))}
    </div>
  )
}

function JobRow({ job: j }: { job: JobRun }) {
  const [open, setOpen] = useState(false)
  const m = parseMetrics(j.metrics_json)

  // Summary for the inline column
  const summary = j.name.startsWith('topics.cluster')
    ? `emb:${m.Embedded ?? m.embedded ?? 0} new:${m.CreatedNew ?? m.created_new ?? 0} assign:${m.AssignedExist ?? m.assigned_exist ?? 0}`
    : j.name.startsWith('topics.label')
      ? `labeled:${m.Labeled ?? m.labeled ?? 0} skip:${m.Skipped ?? m.skipped ?? 0}`
      : m.inserted != null
        ? `+${m.inserted}${(m.updated ?? 0) > 0 ? ` / ~${m.updated}` : ''}`
        : '-'

  return (
    <>
      <Table.Row className="cursor-pointer" onClick={() => setOpen((v) => !v)}>
        <Table.Cell className="font-mono text-xs">
          {sourceSlug(j.name)}
        </Table.Cell>
        <Table.Cell className="whitespace-nowrap text-muted-foreground" title={formatTime(j.started_at)}>
          {relativeTime(j.started_at)}
        </Table.Cell>
        <Table.Cell className="tabular-nums text-muted-foreground">
          {duration(j)}
        </Table.Cell>
        <Table.Cell>{outcomeBadge(j.outcome)}</Table.Cell>
        <Table.Cell className="tabular-nums text-muted-foreground text-xs">
          {summary}
        </Table.Cell>
        <Table.Cell className="text-xs text-destructive max-w-xs truncate" title={j.error}>
          {j.error ? j.error.slice(0, 60) + (j.error.length > 60 ? '...' : '') : ''}
        </Table.Cell>
      </Table.Row>
      {open && (
        <Table.Row className="hover:bg-transparent">
          <Table.Cell colSpan={6} className="bg-muted/30 border-b border-border px-4 py-3">
            <div className="space-y-2 text-xs">
              <div>
                <span className="text-muted-foreground">Job: </span>
                <span className="font-mono text-foreground">{j.name}</span>
                <span className="text-muted-foreground ml-3">ID: </span>
                <span className="text-foreground">{j.id}</span>
                <span className="text-muted-foreground ml-3">Started: </span>
                <span className="text-foreground">{formatTime(j.started_at)}</span>
                <span className="text-muted-foreground ml-3">Duration: </span>
                <span className="text-foreground">{duration(j)}</span>
              </div>
              <div>
                <div className="text-muted-foreground mb-1">Metrics</div>
                <MetricsDetail raw={j.metrics_json} />
              </div>
              {j.error && (
                <div>
                  <div className="text-muted-foreground mb-1">Error</div>
                  <ErrorCell error={j.error} />
                </div>
              )}
            </div>
          </Table.Cell>
        </Table.Row>
      )}
    </>
  )
}

export default function Jobs() {
  const [sp, setSp] = useSearchParams()
  const source = sp.get('source') || ''
  const outcome = sp.get('outcome') || ''
  const page = parseInt(sp.get('page') || '0', 10)

  const setFilter = (updates: Record<string, string>) => {
    const next = new URLSearchParams(sp)
    for (const [k, v] of Object.entries(updates)) {
      if (v && !(k === 'page' && v === '0')) next.set(k, v)
      else next.delete(k)
    }
    if (!('page' in updates)) next.delete('page')
    setSp(next, { replace: true })
  }

  const stats = useJobStats()
  const sources = useSources()
  const jobs = useJobRuns({
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    source: source || undefined,
    outcome: outcome || undefined,
  })

  const total = jobs.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const rows = jobs.data?.data ?? []

  const slugs = (sources.data ?? []).map((s) => s.slug).sort()

  // Source filter — keep optgroup with native select rather than the
  // simpler Select component, since SelectOption[] does not model groups.
  const successRate = stats.data?.success_rate ?? 0

  const outcomeOptions = [
    { label: 'All outcomes', value: '' },
    { label: 'ok', value: 'ok' },
    { label: 'error', value: 'error' },
    { label: 'skipped', value: 'skipped' },
  ]

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">Jobs</h1>

      {stats.error && <ErrorBanner error={stats.error} />}

      <section className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard
          label="Total runs (24h)"
          value={statValue(stats.data?.total_runs, stats.isLoading)}
        />
        <StatCard
          label="Success rate"
          value={stats.data ? `${stats.data.success_rate.toFixed(1)}%` : statValue(undefined, stats.isLoading)}
          tone={stats.data && successRate < 90 ? 'warning' : undefined}
        />
        <StatCard
          label="Items collected (24h)"
          value={statValue(stats.data?.total_items?.toLocaleString('ko-KR'), stats.isLoading)}
        />
        <StatCard
          label="Avg duration"
          value={stats.data ? `${stats.data.avg_duration.toFixed(1)}s` : statValue(undefined, stats.isLoading)}
        />
      </section>

      <section>
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <h2 className="text-sm font-semibold text-foreground mr-auto">
            Job history
          </h2>

          {/* optgroup not supported by Select component — keep raw <select> here */}
          <select
            value={source}
            onChange={(e) => setFilter({ source: e.target.value })}
            className="max-w-[140px] sm:max-w-[160px] rounded-md border border-border bg-card px-2 py-1.5 text-sm text-foreground shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
          >
            <option value="">All jobs</option>
            <option value="topics.cluster">topics.cluster</option>
            <option value="topics.label">topics.label</option>
            <optgroup label="Sources">
              {slugs.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </optgroup>
          </select>

          <Select
            value={outcome}
            onValueChange={(v) => setFilter({ outcome: v })}
            options={outcomeOptions}
            className="max-w-[120px]"
          />

          {jobs.isFetching && (
            <span className="text-xs text-muted-foreground">refreshing…</span>
          )}
        </div>

        {jobs.error && <ErrorBanner error={jobs.error} />}

        <Card className="overflow-x-auto">
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.Header>Source</Table.Header>
                <Table.Header>Started</Table.Header>
                <Table.Header>Duration</Table.Header>
                <Table.Header>Outcome</Table.Header>
                <Table.Header>Collected</Table.Header>
                <Table.Header>Error</Table.Header>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {jobs.isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <Table.Row key={i}>
                    <Table.Cell colSpan={6}>
                      <span className="inline-block h-4 w-full rounded bg-muted animate-pulse" />
                    </Table.Cell>
                  </Table.Row>
                ))
              ) : rows.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={6} className="p-0">
                    <EmptyState title="No job runs found." />
                  </Table.Cell>
                </Table.Row>
              ) : (
                rows.map((j) => <JobRow key={j.id} job={j} />)
              )}
            </Table.Body>
          </Table>
        </Card>

        {totalPages > 1 && (
          <div className="flex flex-wrap items-center justify-between gap-2 mt-2 text-sm text-muted-foreground">
            <span className="text-xs sm:text-sm">
              {total.toLocaleString('ko-KR')}건 중{' '}
              {(page * PAGE_SIZE + 1).toLocaleString('ko-KR')}–
              {Math.min((page + 1) * PAGE_SIZE, total).toLocaleString('ko-KR')}
            </span>
            <Pagination
              page={page + 1}
              totalPages={totalPages}
              onChange={(p) => setFilter({ page: String(p - 1) })}
              disabled={jobs.isFetching}
            />
          </div>
        )}
      </section>
    </div>
  )
}
